/**
 * RFC 7807 problem details, as produced by the backend's global exception filter.
 * Mirrors the contract in `openapi/openapi.json`.
 */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  code: string;
  detail: string;
  instance?: string;
  traceId: string;
  errors?: string[];
}

/**
 * What the UI works with. `message` is already translated for a person to read; `code`
 * and `traceId` are kept so a support conversation can start from an identifier rather
 * than from "it said something went wrong" (PQ-004).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    override readonly message: string,
    readonly traceId: string | null,
    readonly fieldErrors: string[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Whether retrying the same request could plausibly succeed (API-011). */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429 || this.status === 0;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export function isProblemDetails(value: unknown): value is ProblemDetails {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'status' in value &&
    'title' in value
  );
}
